import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getLineServiceDetail } from '../schemes/line_services/actions';
import {
    getLineServiceDetailError,
    getLineServiceDetailLoading,
    getLineServiceDetail as selectLineServiceDetail
} from '../schemes/line_services/selectors';

/**
 * Hook personalizado para gestionar automáticamente la carga del detalle de un servicio
 * 
 * @param {string} idLine - ID de la línea
 * @param {string} slug - Slug del servicio
 * @param {Object} options - Opciones adicionales
 * @param {boolean} options.autoLoad - Si debe cargar automáticamente (default: true)
 * @returns {Object} - { serviceDetail, loading, error, isLoaded, reload }
 */
export const useLineServiceDetail = (idLine, slug, options = {}) => {
  const { autoLoad = true } = options;
  
  const dispatch = useDispatch();
  
  // Referencia para mantener el último serviceDetail válido
  const lastValidServiceDetail = useRef(null);
  
  // Selectors del estado del servicio
  const serviceDetail = useSelector(selectLineServiceDetail);
  const loading = useSelector(getLineServiceDetailLoading);
  const error = useSelector(getLineServiceDetailError);

  // Actualizar la referencia cuando tenemos datos válidos
  useEffect(() => {
    if (serviceDetail && !loading && !error) {
      lastValidServiceDetail.current = serviceDetail;
    }
  }, [serviceDetail, loading, error]);
  
  // Verificar si el servicio actual coincide con los parámetros
  const isCurrentServiceMatching = useMemo(() => {
    if (!idLine || !slug) return false;

    // Usar serviceDetail actual si existe, si no, usar la referencia guardada
    const dataToCheck = serviceDetail || lastValidServiceDetail.current;
    if (!dataToCheck) return false;

    // Verificar directamente si corresponde a nuestros parámetros
    const { service_company } = dataToCheck;
    if (!service_company) return false;

    const matches = service_company.line_uuid === idLine && service_company.slug === slug;
    
    console.log('useLineServiceDetail: Checking match using service_company -', {
      idLine,
      slug,
      hasCurrentData: !!serviceDetail,
      hasRefData: !!lastValidServiceDetail.current,
      serviceCompanyLineUuid: service_company.line_uuid,
      serviceCompanySlug: service_company.slug,
      matches
    });

    return matches;
  }, [serviceDetail, idLine, slug]);
  
  // Determinar si necesitamos cargar el servicio
  const shouldLoadService = useMemo(() => {
    if (!idLine || !slug || !autoLoad) return false;
    if (loading) return false;
    // Solo cargar si no tenemos el servicio correcto
    return !isCurrentServiceMatching;
  }, [idLine, slug, autoLoad, loading, isCurrentServiceMatching]);
  
  // Función para recargar manualmente
  const reload = () => {
    if (idLine && slug) {
      console.log('useLineServiceDetail: Manual reload for service:', idLine, slug);
      dispatch(getLineServiceDetail(idLine, slug));
    }
  };
  
  useEffect(() => {
    if (shouldLoadService) {
      console.log('useLineServiceDetail: Auto-loading service:', idLine, slug);
      dispatch(getLineServiceDetail(idLine, slug));
    }
  }, [dispatch, idLine, slug, shouldLoadService]);

  return {
    serviceDetail: isCurrentServiceMatching ? serviceDetail : null,
    loading,
    error,
    isLoaded: !!isCurrentServiceMatching,
    shouldLoadService,
    reload
  };
};
